import { addDays, format, isValid, parse, startOfISOWeek } from "date-fns";
import type { Locale } from "date-fns/locale";

import { toDateFnsLocale, toIntlLocale } from "@/lib/dates";

/**
 * Date helpers of the market intelligence section. Report and digest dates
 * are plain `date` columns ("YYYY-MM-DD"), handled as local calendar days so
 * no timezone shift ever moves a report to the previous day.
 */

const ISO_DAY = "yyyy-MM-dd";
const ISO_DAY_RX = /^\d{4}-\d{2}-\d{2}$/;

export const dateFnsLocale = (locale: string): Locale =>
  toDateFnsLocale(locale);

export const isIsoDay = (value: unknown): value is string =>
  typeof value === "string" &&
  ISO_DAY_RX.test(value) &&
  isValid(parse(value, ISO_DAY, new Date()));

/** Local calendar day of an ISO date string. */
export const parseIsoDay = (value: string): Date =>
  parse(value.slice(0, 10), ISO_DAY, new Date());

export const toIsoDay = (date: Date): string => format(date, ISO_DAY);

/** Distinct valid dates, most recent first. */
export const sortDatesDesc = (dates: readonly string[]): string[] =>
  [...new Set(dates.filter(isIsoDay))].sort((a, b) => b.localeCompare(a));

export const latestDate = (dates: readonly string[]): string | undefined =>
  sortDatesDesc(dates)[0];

/** The closest available date strictly before `current` (older report). */
export const previousDate = (
  dates: readonly string[],
  current: string,
): string | undefined => sortDatesDesc(dates).find((date) => date < current);

/** The closest available date strictly after `current` (newer report). */
export const nextDate = (
  dates: readonly string[],
  current: string,
): string | undefined =>
  sortDatesDesc(dates)
    .reverse()
    .find((date) => date > current);

/** Monday of the ISO week containing `date`. */
export const weekStartOf = (date: string): string =>
  toIsoDay(startOfISOWeek(parseIsoDay(date)));

/** "miércoles, 4 de noviembre de 2026" (follows the app locale). */
export const formatLongDate = (date: string, locale: string): string =>
  format(parseIsoDay(date), "PPPP", { locale: dateFnsLocale(locale) });

/** "4 nov 2026". */
export const formatShortDate = (date: string, locale: string): string =>
  format(parseIsoDay(date), "PP", { locale: dateFnsLocale(locale) });

/**
 * Working week (Monday to Friday) of a digest: "Semana del 2 al 6 de
 * noviembre de 2026", "Semana del 28 de septiembre al 2 de octubre de 2026".
 */
export const formatWeekLabel = (weekStart: string, locale: string): string => {
  const monday = parseIsoDay(weekStart);
  const friday = addDays(monday, 4);
  const dfLocale = dateFnsLocale(locale);
  const sameYear = monday.getFullYear() === friday.getFullYear();
  const sameMonth = sameYear && monday.getMonth() === friday.getMonth();

  if (locale.startsWith("en")) {
    const range = new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).formatRange(monday, friday);
    return `Week of ${range}`;
  }

  if (locale.startsWith("fr")) {
    const from = format(
      monday,
      sameMonth ? "d" : sameYear ? "d MMMM" : "d MMMM yyyy",
      { locale: dfLocale },
    );
    return `Semaine du ${from} au ${format(friday, "d MMMM yyyy", { locale: dfLocale })}`;
  }

  const from = format(
    monday,
    sameMonth ? "d" : sameYear ? "d 'de' MMMM" : "d 'de' MMMM 'de' yyyy",
    { locale: dfLocale },
  );
  const to = format(friday, "d 'de' MMMM 'de' yyyy", { locale: dfLocale });
  return `Semana del ${from} al ${to}`;
};

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

/** "Mié, 4 nov": the report date in the one-row phone navigation. */
export const formatCompactDate = (date: string, locale: string): string =>
  capitalize(
    new Intl.DateTimeFormat(toIntlLocale(locale), {
      weekday: "short",
      day: "numeric",
      month: "short",
    }).format(parseIsoDay(date)),
  );

/** "2–6 nov": the digest's working week in the one-row phone navigation. */
export const formatCompactWeek = (
  weekStart: string,
  locale: string,
): string => {
  const monday = parseIsoDay(weekStart);
  return new Intl.DateTimeFormat(toIntlLocale(locale), {
    day: "numeric",
    month: "short",
  }).formatRange(monday, addDays(monday, 4));
};
